import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTableToolbarSlot } from './tableToolbarSlot';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Checkbox,
  Menu,
  MenuTrigger,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuItemCheckbox,
  MenuDivider,
  Tooltip,
  Link,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular, MoreHorizontal20Regular, Calendar20Regular, Dismiss12Regular, ColumnEdit20Regular } from '@fluentui/react-icons';
import Pagination from './Pagination';

export interface RowMenuItem {
  key: string;
  label: string;
  icon?: React.ReactElement;
  onClick: (item: any) => void;
  // Per-row visibility. Return true to omit this item for the given row.
  hidden?: (item: any) => boolean;
  // Per-row enablement. Return true to render the item greyed-out and
  // non-clickable (e.g. an action that's illegal for the row's current state).
  // Prefer this over `hidden` when the action should stay visible so the user
  // can see it exists but learn — via `disabledReason` — why it's unavailable.
  disabled?: (item: any) => boolean;
  // Tooltip shown on a disabled item explaining why it's unavailable.
  disabledReason?: (item: any) => string;
  // Render a divider immediately before this item (visual grouping). The
  // divider is suppressed when this would be the first visible item.
  dividerBefore?: boolean;
}

export interface ColumnConfig<T> {
  columnKey: keyof T | 'checkbox';
  label: string;
  sortable?: boolean;
  groupable?: boolean;
  filterable?: boolean; // Enable/disable filtering for this column (default: true)
  width?: string;
  render?: (item: T) => React.ReactNode;
  groupHeaderIcon?: React.ReactElement;
  groupValueFormatter?: (value: string) => string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  keyField: keyof T;
  onSelectionChange?: (selectedKeys: string[]) => void;
  selectedKeys?: string[];
  itemsPerPage?: number;
  showCheckboxes?: boolean;
  onRowClick?: (item: T) => void;
  rowMenuItems?: RowMenuItem[];
  primaryColumnKey?: keyof T;
  disableWrapper?: boolean; // Disable the wrapper surface styling
  syncUrlParams?: boolean; // Persist active filters to URL search params
  defaultSortColumn?: keyof T;
  defaultSortDirection?: SortDirection;
  /** Group rows by this column on first render. The user can clear or
   *  switch grouping via the column-header menu — this only seeds the
   *  initial state. */
  defaultGroupBy?: keyof T;
  /** When grouping is active, also render headers for these group values
   *  even if no row falls under them. Useful when the parent knows the
   *  full set of expected groups (e.g. all product groups in inventory)
   *  but some have no rows. Ignored when grouping is off. */
  additionalGroupKeys?: string[];
  /** Start with all groups expanded instead of collapsed. Ignored when
   *  grouping is off. */
  defaultGroupsExpanded?: boolean;
  /** Show the row count in parentheses after each group header label.
   *  Defaults to true. Ignored when grouping is off. */
  showGroupCount?: boolean;
  /** Let users drag the column headers to rearrange them (desktop table only;
   *  the checkbox column stays pinned first). Off by default so existing
   *  tables are unaffected. */
  reorderableColumns?: boolean;
  /** When set (and reorderableColumns is on), the user's column arrangement is
   *  persisted to localStorage under this key so it survives reloads. Omit for
   *  session-only reordering. */
  columnOrderStorageKey?: string;
  /** Let users drag the right edge of a column header to resize it (desktop
   *  table only). On by default. Initial widths are measured from the natural
   *  auto-layout render, then the table switches to fixed layout so widths are
   *  authoritative. Pass `false` to opt a table out. */
  resizableColumns?: boolean;
  /** When set (and resizableColumns is on), the user's column widths are
   *  persisted to localStorage under this key. Omit for session-only sizing. */
  columnWidthsStorageKey?: string;
  /** Let users show/hide individual columns via a "Columns" button rendered
   *  just above the table. The selection checkbox column is never hideable, and
   *  the user is never allowed to hide every column. Off by default. */
  hideableColumns?: boolean;
  /** When set (and hideableColumns is on), the user's hidden-column choice is
   *  persisted to localStorage under this key. Omit for session-only hiding.
   *  Also scopes the "Reset to default" action, which clears this table's saved
   *  visibility, order and width keys. */
  columnVisibilityStorageKey?: string;
}

type SortDirection = 'ascending' | 'descending';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    // Without minWidth: 0 the flex item's default min-width is the
    // intrinsic content width — i.e. as wide as the table thinks it
    // needs to be. That defeats `.tableContainer`'s overflow-x: auto
    // and pushes the whole page sideways on tablet widths. minWidth: 0
    // lets the wrapper shrink so the inner scroll region works.
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  table: {
    width: '100%',
    // Auto layout — browser sizes each column to fit its content. Avoids
    // the "fixed but no widths" pitfall where every column would split
    // evenly regardless of content. Per-column truncation still works
    // because individual cells set overflow: hidden + textOverflow: ellipsis.
    tableLayout: 'auto',
  },
  // Resizable instances switch to fixed layout once widths are seeded so the
  // per-column pixel widths are authoritative. width: max-content lets the
  // table grow past the container (the container scrolls); minWidth keeps it
  // filling the viewport when the columns are narrower than the container.
  resizableTable: {
    tableLayout: 'fixed',
    width: 'max-content',
    minWidth: '100%',
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: '6px',
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
    zIndex: 1,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground,
      opacity: 0.5,
    },
  },
  headerCell: {
    fontWeight: 'bold',
    position: 'relative',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    cursor: 'pointer',
    ...shorthands.padding('0', '8px', '0', '0'),
  },
  // Reorder affordances — applied to the header cell when reorderableColumns is on.
  reorderableHeader: {
    cursor: 'grab',
    userSelect: 'none',
  },
  draggingHeader: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  dragOverHeader: {
    // Insertion indicator: a brand-colored left edge marks where the dragged
    // column will land.
    boxShadow: `inset 2px 0 0 0 ${tokens.colorBrandBackground}`,
  },
  headerLabel: {
    flex: 1,
  },
  textSortIndicator: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    marginLeft: '6px',
    letterSpacing: '0.02em',
  },
  tableCell: {
    ...shorthands.overflow('hidden'),
    ...shorthands.padding('0'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cellContent: {
    display: 'block',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...shorthands.padding('8px', '12px'),
  },
  clickableRow: {
    cursor: 'default',
    '&:hover:not([data-hover-card-active])': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
  },
  actionsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',

    ...shorthands.padding('8px', '16px'),
    ...shorthands.borderRadius('4px'),
    marginTop: '16px',
    marginBottom: '8px',
    // Align the toolbar gutter with the cards (12px) and tighten the top
    // margin on mobile.
    '@media (max-width: 768px)': {
      ...shorthands.padding('8px', '12px'),
      marginTop: '8px',
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('8px', '16px'),
    zIndex: 1,
    // Trim the footer chrome on phones — the pagination row already drops its
    // own vertical padding, so collapse this to a hairline of breathing room.
    // The fixed MobileBottomNav (56px + safe area) covers the scrollport's
    // bottom, so rather than lift the sticky stop (which exposes a strip of
    // list content between the footer and the nav), keep it stuck at bottom:0
    // and pad the footer's own bottom by the nav height. The pagination row
    // then sits just above the nav and the footer's opaque background hides
    // the content that would otherwise peek through. The visible band above
    // the nav uses a matching 2px top + 2px-over-nav bottom so the pagination
    // controls sit evenly between the card divider and the nav.
    '@media (max-width: 768px)': {
      ...shorthands.padding(
        '2px',
        '8px',
        'calc(56px + env(safe-area-inset-bottom) + 2px)',
        '8px',
      ),
      bottom: '0',
    },
  },
  groupHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: 600,
    fontSize: '14px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    position: 'sticky',
    top: 0,
    zIndex: 1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  groupHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  groupExpandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground2,
  },
  groupHeaderIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground2,
  },
  checkboxCell: {
    width: '50px',
    minWidth: '50px',
    maxWidth: '50px',
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  ellipsisCell: {
    width: '48px',
    minWidth: '48px',
    maxWidth: '48px',
    textAlign: 'right',
  },
  ellipsisButton: {
    transition: 'opacity 0.2s, background-color 0.2s',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('4px'),
    ...shorthands.borderRadius('4px'),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    // Keep the keyboard ring; drop the stray outline on mouse/touch and on the
    // focus restore when the menu closes (the source of the blue flash).
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    '&:focus:not(:focus-visible)': {
      outlineStyle: 'none',
    },
  },
  ellipsisButtonHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  ellipsisButtonVisible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  primaryCell: {
    cursor: 'pointer',
  },
  primaryCellContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    ...shorthands.padding('8px', '12px'),
  },
  primaryCellMain: {
    flex: 1,
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  primaryCellMenu: {
    flexShrink: 0,
  },
  // Thin bar above the table holding the "Columns" show/hide button, pushed to
  // the right. Only rendered when hideableColumns is opted into.
  columnsToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.padding('0', '0', '8px', '0'),
  },
  activeFiltersBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground3,
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  filterPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: '12px',
    fontWeight: 500,
  },
  filterPillButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '14px',
    height: '14px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    cursor: 'pointer',
    color: tokens.colorBrandForeground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundInverted,
    },
  },
  clearAllPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    color: tokens.colorNeutralForeground1,
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    // Card gutter on mobile — the work surface is full-bleed, but the cards
    // keep a 12px inset so they read as floating list items with breathing
    // room rather than edge-glued bands.
    ...shorthands.padding('8px', '12px', '12px'),
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  mobileCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('12px'),
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    // Keyboard focus gets a ring; mouse/touch (and programmatic focus restore
    // when the row menu closes) does NOT — that stray outline read as the card
    // "flashing blue" when opening the ⋯ menu.
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    ':focus:not(:focus-visible)': {
      outlineStyle: 'none',
    },
  },
  mobileCardHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    marginBottom: '8px',
  },
  mobileCardTitle: {
    flex: 1,
    fontSize: '15px',
    fontWeight: 600,
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mobileCardRow: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: '12px',
    ...shorthands.padding('3px', '0'),
    minWidth: 0,
  },
  mobileCardLabel: {
    flexShrink: 0,
    width: '40%',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  mobileCardValue: {
    flex: 1,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  mobileGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    ...shorthands.padding('12px', '4px', '6px'),
    cursor: 'pointer',
    userSelect: 'none',
  },
  mobileEmpty: {
    ...shorthands.padding('24px', '12px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  keyField,
  onSelectionChange,
  selectedKeys: externalSelectedKeys,
  itemsPerPage = 10,
  showCheckboxes = true,
  onRowClick,
  rowMenuItems,
  primaryColumnKey,
  disableWrapper = false,
  syncUrlParams = false,
  defaultSortColumn,
  defaultSortDirection = 'ascending',
  defaultGroupBy,
  additionalGroupKeys,
  defaultGroupsExpanded = false,
  showGroupCount = true,
  reorderableColumns = false,
  columnOrderStorageKey,
  resizableColumns = true,
  columnWidthsStorageKey,
  hideableColumns = false,
  columnVisibilityStorageKey,
}: DataTableProps<T>) {
  const styles = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  // When hosted in a TablePage, render the "Columns" toggle into its search
  // row instead of our own inline toolbar (null when standalone).
  const toolbarSlot = useTableToolbarSlot();
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync internal state with external selectedKeys prop
  useEffect(() => {
    if (externalSelectedKeys !== undefined) {
      setSelectedKeys(new Set(externalSelectedKeys));
    }
  }, [externalSelectedKeys]);
  const [sortColumn, setSortColumn] = useState<keyof T | null>(defaultSortColumn ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupByColumn, setGroupByColumn] = useState<keyof T | null>(defaultGroupBy ?? null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Column reorder (opt-in via reorderableColumns). `columnOrder` is the
  // user-arranged list of non-checkbox column keys; null means "use the order
  // the columns prop was passed in". Drag state tracks the in-flight gesture so
  // the source can dim and the drop target can show an insertion edge.
  const [columnOrder, setColumnOrder] = useState<string[] | null>(null);
  const [dragColumnKey, setDragColumnKey] = useState<string | null>(null);
  const [dragOverColumnKey, setDragOverColumnKey] = useState<string | null>(null);
  // Column visibility (opt-in via hideableColumns). Set of hidden non-checkbox
  // column keys; empty = every column visible.
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  // Column resize (opt-in via resizableColumns). `columnWidths` maps columnKey
  // -> pixel width; empty until seeded. `resizingKey` is the column currently
  // being dragged (also used to suspend reorder-drag while resizing). Header
  // cell elements are tracked in a ref so initial widths can be measured from
  // the natural auto-layout render before switching to fixed layout.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const headerCellRefs = useRef<Record<string, HTMLElement | null>>({});
  const widthsSeededRef = useRef(false);
  const MIN_COLUMN_WIDTH = 60;
  const [columnFilters, setColumnFilters] = useState<Map<string, Set<string>>>(() => {
    if (!syncUrlParams) return new Map();
    const filters = new Map<string, Set<string>>();
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_')) {
        const columnKey = key.slice(7);
        const values = value.split('|').filter(Boolean);
        if (values.length > 0) filters.set(columnKey, new Set(values));
      }
    });
    return filters;
  });


  const handleSort = (columnKey: string, direction: 'ascending' | 'descending') => {
    if (columnKey === 'checkbox') return;

    const column = columns.find(col => col.columnKey === columnKey);
    if (!column?.sortable) return;

    setSortColumn(columnKey as keyof T);
    setSortDirection(direction);
  };

  const handleGroupBy = (columnKey: string) => {
    if (columnKey === 'checkbox') return;

    const currentGroupKey = groupByColumn as string;
    if (currentGroupKey === columnKey) {
      // Toggle off if already grouped by this column
      setGroupByColumn(null);
    } else {
      setGroupByColumn(columnKey as keyof T);
    }
  };


  const sortedData = useMemo(() => {
    let processedData = [...data];

    // Apply column filters first
    if (columnFilters.size > 0) {
      processedData = processedData.filter(item => {
        // Item must match ALL active filters (AND logic)
        const filters = Array.from(columnFilters.entries());
        for (let i = 0; i < filters.length; i++) {
          const [columnKey, allowedValues] = filters[i];
          const itemValue = item[columnKey as keyof T];
          const stringValue = itemValue != null ? String(itemValue) : '(Empty)';

          if (!allowedValues.has(stringValue)) {
            return false; // Item doesn't match this filter
          }
        }
        return true; // Item matches all filters
      });
    }

    // Apply sorting
    if (sortColumn) {
      processedData = processedData.sort((a, b) => {
        let aValue: any = a[sortColumn];
        let bValue: any = b[sortColumn];

        // Always push nullish/empty values to the end regardless of direction
        const aMissing = aValue == null || aValue === '';
        const bMissing = bValue == null || bValue === '';
        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;

        // Handle date sorting (Date instances)
        if (aValue instanceof Date || bValue instanceof Date) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        // Handle numeric string sorting (e.g., order numbers like "1", "2", "10")
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);

          // Only treat as numeric if the entire string is a clean number — otherwise
          // ISO timestamps like "2026-04-15T00:00:00Z" silently parse to year 2026
          // and date columns appear unsorted. ISO strings then fall through to the
          // case-insensitive branch, which sorts them chronologically because ISO 8601
          // is lexicographically ordered.
          const aIsPureNum = !isNaN(aNum) && String(aNum) === aValue.trim();
          const bIsPureNum = !isNaN(bNum) && String(bNum) === bValue.trim();

          if (aIsPureNum && bIsPureNum) {
            aValue = aNum;
            bValue = bNum;
          } else {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
        }

        if (aValue < bValue) return sortDirection === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    // Apply grouping
    if (groupByColumn) {
      processedData = processedData.sort((a, b) => {
        let aValue: any = a[groupByColumn];
        let bValue: any = b[groupByColumn];

        // Handle string comparison for grouping
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      });
    }

    return processedData;
  }, [data, sortColumn, sortDirection, groupByColumn, columnFilters]);

  // Helper function to get group header icon
  const getGroupHeaderIcon = (columnKey: keyof T): React.ReactElement | null => {
    // Check if column has a custom group header icon
    const column = columns.find(col => col.columnKey === columnKey);
    if (column?.groupHeaderIcon) {
      return column.groupHeaderIcon;
    }

    // Auto-detect date columns and show calendar icon
    if (isDateColumn(columnKey)) {
      return <Calendar20Regular />;
    }

    return null;
  };

  // Helper function to format group header labels
  const formatGroupLabel = (groupValue: string, columnKey: keyof T): string => {
    const column = columns.find(c => c.columnKey === columnKey);
    if (column?.groupValueFormatter) {
      return column.groupValueFormatter(groupValue);
    }

    const isDate = isDateColumn(columnKey);
    if (isDate && groupValue !== 'Ungrouped') {
      try {
        const date = new Date(groupValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-CA');
        }
      } catch {
        return groupValue;
      }
    }

    return groupValue;
  };

  // Group data if grouping is enabled
  const groupedData = useMemo(() => {
    if (!groupByColumn) return null;

    const groups: Record<string, T[]> = {};
    sortedData.forEach(item => {
      const groupValue = String(item[groupByColumn] || 'Ungrouped');
      if (!groups[groupValue]) {
        groups[groupValue] = [];
      }
      groups[groupValue].push(item);
    });

    // Seed empty buckets for any explicitly-declared group keys so they
    // still render a header (e.g. product groups with no stock items).
    if (additionalGroupKeys) {
      for (const key of additionalGroupKeys) {
        if (!groups[key]) groups[key] = [];
      }
    }

    return groups;
  }, [sortedData, groupByColumn, additionalGroupKeys]);

  // Reset expanded groups when grouping changes. Default to collapsed unless
  // the parent opted into `defaultGroupsExpanded`, in which case seed every
  // current group as expanded.
  useEffect(() => {
    if (defaultGroupsExpanded && groupedData) {
      setExpandedGroups(new Set(Object.keys(groupedData)));
    } else {
      setExpandedGroups(new Set());
    }
  }, [groupedData, defaultGroupsExpanded]);

  // When grouping toggles on, reset the page so re-entering ungrouped mode
  // doesn't land the user on a phantom page (groups bypass pagination).
  useEffect(() => {
    if (groupByColumn) setCurrentPage(1);
  }, [groupByColumn]);

  // Toggle group expansion
  const toggleGroupExpansion = (groupKey: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(groupKey)) {
      newExpandedGroups.delete(groupKey);
    } else {
      newExpandedGroups.add(groupKey);
    }
    setExpandedGroups(newExpandedGroups);
  };

  // Calculate pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select only items on the current page
      const pageKeys = paginatedData.map(item => String(item[keyField]));
      const newSelectedKeys = new Set(selectedKeys);
      pageKeys.forEach(key => newSelectedKeys.add(key));
      setSelectedKeys(newSelectedKeys);
      onSelectionChange?.(Array.from(newSelectedKeys));
    } else {
      // Deselect only items on the current page
      const pageKeys = new Set(paginatedData.map(item => String(item[keyField])));
      const newSelectedKeys = new Set(selectedKeys);
      pageKeys.forEach(key => newSelectedKeys.delete(key));
      setSelectedKeys(newSelectedKeys);
      onSelectionChange?.(Array.from(newSelectedKeys));
    }
  };

  const handleSelectOne = (key: string, checked: boolean) => {
    const newSelectedKeys = new Set(selectedKeys);
    if (checked) {
      newSelectedKeys.add(key);
    } else {
      newSelectedKeys.delete(key);
    }
    setSelectedKeys(newSelectedKeys);
    onSelectionChange?.(Array.from(newSelectedKeys));
  };

  // Detect if a column contains date values
  const isDateColumn = (columnKey: keyof T): boolean => {
    if (data.length === 0) return false;

    // Check the first 10 items (or all if fewer)
    const samplesToCheck = Math.min(10, data.length);
    let dateCount = 0;
    let nonNullCount = 0;

    for (let i = 0; i < samplesToCheck; i++) {
      const value: any = data[i][columnKey];

      // Skip null/undefined values — they're not informative either way.
      // Crucially, we don't count them in the denominator, so a sparse
      // column (e.g. latest_order_at, where many customers have no orders)
      // still gets classified correctly when its non-null values are dates.
      if (value == null) continue;
      nonNullCount++;

      if (value instanceof Date) {
        dateCount++;
        continue;
      }

      if (typeof value === 'string') {
        const parsed = new Date(value);
        // Require an ISO-ish shape so non-date strings (names, emails,
        // notes that happen to be parseable) don't trigger a false
        // positive. Date.parse on "Bob" is NaN, but on "1" is a year.
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(value);
        if (looksLikeDate && !isNaN(parsed.getTime())) {
          dateCount++;
        }
      }
    }

    // If we have any non-null samples and most of them are dates, treat
    // the column as a date column.
    return nonNullCount > 0 && dateCount / nonNullCount > 0.5;
  };

  // Detect if a column contains numerical values
  const isNumberColumn = (columnKey: keyof T): boolean => {
    if (data.length === 0) return false;

    // Check the first 10 items (or all if fewer)
    const samplesToCheck = Math.min(10, data.length);
    let numberCount = 0;

    for (let i = 0; i < samplesToCheck; i++) {
      const value: any = data[i][columnKey];

      // Skip null/undefined values
      if (value == null) continue;

      // Check if it's a number
      if (typeof value === 'number' && !isNaN(value)) {
        numberCount++;
      }
    }

    // If more than 50% of checked values are numbers, consider it a number column
    return numberCount > samplesToCheck / 2;
  };

  // Per-column type detection (date / number) memoized on [columns, data]. The
  // header render loop and the sort menu consult this for every column, and the
  // table re-renders frequently on internal state (row hover, selection,
  // pagination) where columns + data are unchanged — so sample the rows once per
  // data change instead of re-sampling every column on every render.
  const columnTypes = useMemo(() => {
    const types: Record<string, { isDate: boolean; isNumber: boolean }> = {};
    for (const col of columns) {
      if (col.columnKey === 'checkbox') continue;
      const isDate = isDateColumn(col.columnKey as keyof T);
      types[String(col.columnKey)] = {
        isDate,
        isNumber: !isDate && isNumberColumn(col.columnKey as keyof T),
      };
    }
    return types;
    // isDateColumn/isNumberColumn close over `data`; recompute when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, data]);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  // Seed the saved column arrangement from localStorage (when persistence is
  // opted into). Malformed storage is ignored so a bad value can never wedge
  // the table — it just falls back to the prop order.
  useEffect(() => {
    if (!reorderableColumns || !columnOrderStorageKey) return;
    try {
      const raw = localStorage.getItem(`dt_colorder_${columnOrderStorageKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setColumnOrder(parsed.map(String));
      }
    } catch {
      /* ignore unavailable/corrupt storage */
    }
  }, [reorderableColumns, columnOrderStorageKey]);

  // Seed the saved hidden-column set from localStorage (when persistence is
  // opted into). Malformed storage is ignored so a bad value can never wedge the
  // table — it just falls back to "all visible".
  useEffect(() => {
    if (!hideableColumns || !columnVisibilityStorageKey) return;
    try {
      const raw = localStorage.getItem(`dt_colhidden_${columnVisibilityStorageKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setHiddenColumns(new Set(parsed.map(String)));
      }
    } catch {
      /* ignore unavailable/corrupt storage */
    }
  }, [hideableColumns, columnVisibilityStorageKey]);

  // Persist the hidden-column choice (best-effort) whenever it changes.
  useEffect(() => {
    if (!hideableColumns || !columnVisibilityStorageKey) return;
    try {
      localStorage.setItem(
        `dt_colhidden_${columnVisibilityStorageKey}`,
        JSON.stringify(Array.from(hiddenColumns)),
      );
    } catch {
      /* storage unavailable — the choice still applies for this session */
    }
  }, [hiddenColumns, hideableColumns, columnVisibilityStorageKey]);

  // Listen for column filter events
  useEffect(() => {
    const handleApplyFilter = (event: CustomEvent) => {
      const { columnKey, selectedValues } = event.detail;
      const newFilters = new Map(columnFilters);

      if (selectedValues.length > 0) {
        newFilters.set(columnKey, new Set(selectedValues));
      } else {
        newFilters.delete(columnKey);
      }

      setColumnFilters(newFilters);
      setCurrentPage(1); // Reset to first page when filter changes
    };

    window.addEventListener('applyColumnFilter', handleApplyFilter as EventListener);
    return () => {
      window.removeEventListener('applyColumnFilter', handleApplyFilter as EventListener);
    };
  }, [columnFilters]);

  // Sync active filters to URL search params
  useEffect(() => {
    if (!syncUrlParams) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // Clear existing filter params
      Array.from(next.keys())
        .filter(k => k.startsWith('filter_'))
        .forEach(k => next.delete(k));
      // Write current filters
      columnFilters.forEach((values, columnKey) => {
        if (values.size > 0) {
          next.set(`filter_${columnKey}`, Array.from(values).join('|'));
        }
      });
      return next;
    }, { replace: true });
  }, [columnFilters, syncUrlParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Check if all items on the current page are selected
  const allCurrentPageSelected = paginatedData.length > 0 &&
    paginatedData.every(item => selectedKeys.has(String(item[keyField])));

  // Determine primary column (first non-checkbox column if not specified)
  const effectivePrimaryColumn = useMemo(() => {
    if (primaryColumnKey) return primaryColumnKey;
    const firstNonCheckboxColumn = columns.find(col => col.columnKey !== 'checkbox');
    return firstNonCheckboxColumn?.columnKey as keyof T;
  }, [primaryColumnKey, columns]);

  // Filter columns to display: drop the checkbox column when selection is off,
  // and drop any user-hidden columns (the checkbox column is never hideable).
  const displayColumns = (showCheckboxes ? columns : columns.filter(col => col.columnKey !== 'checkbox'))
    .filter(col => col.columnKey === 'checkbox' || !hiddenColumns.has(String(col.columnKey)));

  // Every non-checkbox column is hideable; the menu lists them all (hidden ones
  // appear unchecked). `visibleToggleableKeys` feeds the MenuItemCheckbox group.
  const toggleableColumns = useMemo(
    () => columns.filter(col => col.columnKey !== 'checkbox'),
    [columns],
  );
  const visibleToggleableKeys = toggleableColumns
    .filter(col => !hiddenColumns.has(String(col.columnKey)))
    .map(col => String(col.columnKey));

  const handleColumnVisibilityChange = (
    _e: unknown,
    data: { name: string; checkedItems: string[] },
  ) => {
    if (data.name !== 'columns') return;
    // Guard: never let the user hide every data column (the table would go blank
    // and the menu would have no way back).
    if (data.checkedItems.length === 0) return;
    const checked = new Set(data.checkedItems);
    const nextHidden = new Set<string>();
    toggleableColumns.forEach(col => {
      const key = String(col.columnKey);
      if (!checked.has(key)) nextHidden.add(key);
    });
    setHiddenColumns(nextHidden);
  };

  // Restore the out-of-the-box view for this table: all columns visible, and the
  // saved order + widths cleared (DataTable owns all three, so no remount needed).
  const resetColumnsToDefault = () => {
    setHiddenColumns(new Set());
    setColumnOrder(null);
    setColumnWidths({});
    widthsSeededRef.current = false;
    try {
      if (columnVisibilityStorageKey) localStorage.removeItem(`dt_colhidden_${columnVisibilityStorageKey}`);
      if (columnOrderStorageKey) localStorage.removeItem(`dt_colorder_${columnOrderStorageKey}`);
      if (columnWidthsStorageKey) localStorage.removeItem(`dt_colwidth_${columnWidthsStorageKey}`);
    } catch {
      /* ignore */
    }
  };

  // Apply the user's column arrangement when reordering is enabled. The
  // checkbox column is always pinned first; any column missing from a saved
  // order (e.g. one added since the order was stored, or one removed) is
  // appended in its original position so the set self-heals and a column never
  // silently disappears.
  const columnsWithMenu = useMemo(() => {
    if (!reorderableColumns || !columnOrder) return displayColumns;
    const byKey = new Map(displayColumns.map(c => [String(c.columnKey), c]));
    const result: ColumnConfig<T>[] = [];
    const checkbox = displayColumns.find(c => c.columnKey === 'checkbox');
    if (checkbox) result.push(checkbox);
    columnOrder.forEach(key => {
      if (key === 'checkbox') return;
      const col = byKey.get(key);
      if (col) result.push(col);
    });
    displayColumns.forEach(col => {
      if (col.columnKey === 'checkbox') return;
      if (!columnOrder.includes(String(col.columnKey))) result.push(col);
    });
    return result;
  }, [displayColumns, columnOrder, reorderableColumns]);

  // Persist the arrangement (best-effort) when a storage key is provided.
  const persistColumnOrder = (order: string[]) => {
    if (!columnOrderStorageKey) return;
    try {
      localStorage.setItem(`dt_colorder_${columnOrderStorageKey}`, JSON.stringify(order));
    } catch {
      /* storage full/unavailable — the order still applies for this session */
    }
  };

  // Move the dragged column so it lands in front of the drop-target column.
  const handleColumnDrop = (targetKey: string) => {
    const sourceKey = dragColumnKey;
    setDragColumnKey(null);
    setDragOverColumnKey(null);
    if (!sourceKey || sourceKey === targetKey || targetKey === 'checkbox') return;
    const current = columnsWithMenu
      .filter(c => c.columnKey !== 'checkbox')
      .map(c => String(c.columnKey));
    const from = current.indexOf(sourceKey);
    const to = current.indexOf(targetKey);
    if (from === -1 || to === -1) return;
    current.splice(from, 1);
    current.splice(to, 0, sourceKey);
    setColumnOrder(current);
    persistColumnOrder(current);
  };

  // Seed column widths once when resizing is enabled: prefer a persisted set,
  // otherwise measure the natural (auto-layout) header widths. useLayoutEffect
  // so it runs before paint — the user never sees the pre-fixed-layout state.
  useLayoutEffect(() => {
    if (!resizableColumns || widthsSeededRef.current) return;
    if (columnWidthsStorageKey) {
      try {
        const raw = localStorage.getItem(`dt_colwidth_${columnWidthsStorageKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            setColumnWidths(parsed);
            widthsSeededRef.current = true;
            return;
          }
        }
      } catch {
        /* ignore corrupt storage — fall through to measuring */
      }
    }
    const measured: Record<string, number> = {};
    columnsWithMenu.forEach((c) => {
      const el = headerCellRefs.current[String(c.columnKey)];
      if (el) measured[String(c.columnKey)] = Math.round(el.getBoundingClientRect().width);
    });
    if (Object.keys(measured).length > 0) {
      setColumnWidths(measured);
      widthsSeededRef.current = true;
    }
  }, [resizableColumns, columnWidthsStorageKey, columnsWithMenu]);

  // Persist widths (best-effort) whenever they change.
  useEffect(() => {
    if (!resizableColumns || !columnWidthsStorageKey) return;
    if (Object.keys(columnWidths).length === 0) return;
    try {
      localStorage.setItem(`dt_colwidth_${columnWidthsStorageKey}`, JSON.stringify(columnWidths));
    } catch {
      /* storage unavailable — widths still apply for this session */
    }
  }, [columnWidths, resizableColumns, columnWidthsStorageKey]);

  // Begin a resize drag from a header's right-edge handle. Window-level mouse
  // listeners keep the drag alive even when the cursor leaves the handle.
  const startColumnResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth =
      columnWidths[key] ??
      headerCellRefs.current[key]?.getBoundingClientRect().width ??
      120;
    setResizingKey(key);
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + (ev.clientX - startX)));
      setColumnWidths((prev) => ({ ...prev, [key]: next }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setResizingKey(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Width applied to a column's <col>: a resized/seeded width wins, then any
  // static column.width, else undefined (auto).
  const widthForColumn = (column: ColumnConfig<T>): string | undefined => {
    const key = String(column.columnKey);
    if (resizableColumns && columnWidths[key] != null) return `${columnWidths[key]}px`;
    return column.width;
  };

  // Only switch to fixed layout once widths exist, so the initial measure
  // happens against the natural auto-layout render.
  const widthsReady = resizableColumns && Object.keys(columnWidths).length > 0;

  const handleSelectAllData = () => {
    const allKeys = new Set(sortedData.map(item => String(item[keyField])));
    setSelectedKeys(allKeys);
    onSelectionChange?.(Array.from(allKeys));
  };

  // Helper to get column label by key
  const getColumnLabel = (columnKey: string): string => {
    const column = columns.find(col => String(col.columnKey) === columnKey);
    return column?.label || columnKey;
  };

  // Remove a specific filter value
  const removeFilter = (columnKey: string, valueToRemove: string) => {
    const newFilters = new Map(columnFilters);
    const currentValues = newFilters.get(columnKey);

    if (currentValues) {
      currentValues.delete(valueToRemove);
      if (currentValues.size === 0) {
        newFilters.delete(columnKey);
      }
    }

    setColumnFilters(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters(new Map());
  };

  // Helper function to render ellipsis menu
  const renderEllipsisMenu = (item: T, isRowHovered: boolean) => {
    if (!rowMenuItems || rowMenuItems.length === 0) return null;

    const visibleItems = rowMenuItems.filter((mi) => !mi.hidden?.(item));
    if (visibleItems.length === 0) return null;

    return (
      <div className={styles.primaryCellMenu}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <div
              role="button"
              tabIndex={0}
              aria-label="More actions"
              className={`${styles.ellipsisButton} ${
                isRowHovered ? styles.ellipsisButtonVisible : styles.ellipsisButtonHidden
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal20Regular />
            </div>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {visibleItems.map((menuItem, idx) => {
                const isDisabled = menuItem.disabled?.(item) ?? false;
                const reason = isDisabled ? menuItem.disabledReason?.(item) : undefined;
                const menuEntry = (
                  <MenuItem
                    key={menuItem.key}
                    icon={menuItem.icon}
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      menuItem.onClick(item);
                    }}
                  >
                    {menuItem.label}
                  </MenuItem>
                );
                return (
                  <React.Fragment key={menuItem.key}>
                    {menuItem.dividerBefore && idx > 0 && <MenuDivider />}
                    {reason ? (
                      // Disabled MenuItems don't fire pointer events, so the
                      // Tooltip wraps a block span that still receives hover.
                      // `description` (not `label`) keeps the item's own name as
                      // its accessible name and adds the reason as a description.
                      <Tooltip content={reason} relationship="description" withArrow>
                        <span style={{ display: 'block' }}>{menuEntry}</span>
                      </Tooltip>
                    ) : (
                      menuEntry
                    )}
                  </React.Fragment>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    );
  };

  const renderMobileCard = (item: T) => {
    const itemKey = String(item[keyField]);
    const titleColumn = displayColumns.find(c => c.columnKey === effectivePrimaryColumn)
      ?? displayColumns.find(c => c.columnKey !== 'checkbox');
    const restColumns = displayColumns.filter(
      c => c.columnKey !== 'checkbox' && c.columnKey !== titleColumn?.columnKey
    );

    return (
      <div
        key={itemKey}
        className={styles.mobileCard}
        onClick={onRowClick ? () => onRowClick(item) : undefined}
        role={onRowClick ? 'button' : undefined}
        tabIndex={onRowClick ? 0 : undefined}
        onKeyDown={onRowClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick(item);
          }
        } : undefined}
      >
        <div className={styles.mobileCardHeader}>
          {showCheckboxes && (
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedKeys.has(itemKey)}
                onChange={(_, data) => handleSelectOne(itemKey, data.checked === true)}
              />
            </span>
          )}
          <div className={styles.mobileCardTitle}>
            {titleColumn?.render
              ? titleColumn.render(item)
              : titleColumn
                ? String(item[titleColumn.columnKey as keyof T] ?? '')
                : null}
          </div>
          {rowMenuItems && rowMenuItems.length > 0 && (
            <span onClick={(e) => e.stopPropagation()}>
              {renderEllipsisMenu(item, true)}
            </span>
          )}
        </div>
        {restColumns.map(column => (
          <div key={String(column.columnKey)} className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>{column.label}</span>
            <span className={styles.mobileCardValue}>
              {column.render ? column.render(item) : String(item[column.columnKey as keyof T] ?? '')}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Show/hide columns menu (opt-in). Self-contained: state, persistence and the
  // "Reset to default" action all live in DataTable so any table gets the
  // feature by setting hideableColumns. Rendered either inline (standalone) or
  // portaled into a TablePage's search row (see toolbarSlot).
  const columnsMenu = hideableColumns ? (
    <Menu
      checkedValues={{ columns: visibleToggleableKeys }}
      onCheckedValueChange={handleColumnVisibilityChange}
      persistOnItemClick
    >
      <MenuTrigger disableButtonEnhancement>
        <MenuButton size="small" appearance="subtle" icon={<ColumnEdit20Regular />}>
          Columns
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {toggleableColumns.map(col => (
            <MenuItemCheckbox
              key={String(col.columnKey)}
              name="columns"
              value={String(col.columnKey)}
            >
              {col.label || String(col.columnKey)}
            </MenuItemCheckbox>
          ))}
          <MenuDivider />
          <MenuItem onClick={resetColumnsToDefault}>Reset to default</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  ) : null;

  const tableContent = (
    <>
      {/* Columns toggle placement:
          - inside a TablePage host (toolbarSlot !== undefined): portal into its
            search-row slot once it's mounted; render nothing while pending
            (null) so the inline bar never flashes.
          - standalone (toolbarSlot === undefined): render the inline toolbar. */}
      {columnsMenu && (toolbarSlot !== undefined
        ? (toolbarSlot ? createPortal(columnsMenu, toolbarSlot) : null)
        : <div className={styles.columnsToolbar}>{columnsMenu}</div>)}

      {/* Active Filters Bar */}
      {columnFilters.size > 0 && (
          <div className={styles.activeFiltersBar}>
            <span className={styles.filterLabel}>Active Filters:</span>
            {Array.from(columnFilters.entries()).map(([columnKey, values]) => {
              const columnLabel = getColumnLabel(columnKey);
              return Array.from(values).map((value) => (
                <div key={`${columnKey}-${value}`} className={styles.filterPill}>
                  <span>
                    {columnLabel}: {value}
                  </span>
                  <button
                    className={styles.filterPillButton}
                    onClick={() => removeFilter(columnKey, value)}
                    aria-label={`Remove filter: ${columnLabel} = ${value}`}
                  >
                    <Dismiss12Regular />
                  </button>
                </div>
              ));
            })}
            <button
              className={styles.clearAllPill}
              onClick={clearAllFilters}
              aria-label="Clear all filters"
            >
              Clear all
            </button>
          </div>
        )}

        {isMobile ? (
          <div className={styles.mobileList}>
            {sortedData.length === 0 ? (
              <div className={styles.mobileEmpty}>No data found</div>
            ) : groupedData ? (
              Object.entries(groupedData).map(([groupValue, groupItems]) => {
                const isExpanded = expandedGroups.has(groupValue);
                return (
                  <React.Fragment key={groupValue}>
                    <div
                      className={styles.mobileGroupHeader}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      onClick={() => toggleGroupExpansion(groupValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleGroupExpansion(groupValue);
                        }
                      }}
                    >
                      {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                      <span>
                        {formatGroupLabel(groupValue, groupByColumn!)}
                        {showGroupCount ? ` (${groupItems.length})` : ''}
                      </span>
                    </div>
                    {isExpanded && groupItems.map(renderMobileCard)}
                  </React.Fragment>
                );
              })
            ) : (
              paginatedData.map(renderMobileCard)
            )}
          </div>
        ) : (
        <div className={styles.tableContainer}>
          <Table sortable className={mergeClasses(styles.table, widthsReady && styles.resizableTable)}>
            {/* Lock per-column widths via colgroup so filtering doesn't reflow
                the layout when the longest cell in a column drops out of the
                visible set. Columns with an explicit `width` (or a user-resized
                width) get a hard width — others stay auto-sized. */}
            <colgroup>
              {columnsWithMenu.map((column) => {
                const colWidth = widthForColumn(column);
                return (
                  <col
                    key={String(column.columnKey)}
                    style={colWidth ? { width: colWidth } : undefined}
                  />
                );
              })}
            </colgroup>
            <TableHeader>
              <TableRow>
                {columnsWithMenu.map((column) => {
                  const columnKey = String(column.columnKey);
                  // Suspend reorder-drag while a resize is in progress so the
                  // two gestures can't fire at once.
                  const isDraggable = reorderableColumns && column.columnKey !== 'checkbox' && resizingKey === null;
                  const isResizable = resizableColumns && column.columnKey !== 'checkbox';
                  const isSorted = sortColumn === column.columnKey;
                  const colType = columnTypes[columnKey];
                  const isTextColumn =
                    column.columnKey !== 'checkbox' &&
                    !colType?.isDate &&
                    !colType?.isNumber;
                  const textSortLabel =
                    isSorted && isTextColumn
                      ? sortDirection === 'ascending' ? 'A–Z' : 'Z–A'
                      : null;

                  return (
                    <TableHeaderCell
                      key={columnKey}
                      ref={(el: HTMLElement | null) => { headerCellRefs.current[columnKey] = el; }}
                      // Render the resize handle via the `aside` slot so it sits
                      // directly in the <th> (at the cell's true right edge),
                      // not inside Fluent's content-width sort-button wrapper.
                      aside={isResizable ? (
                        <div
                          className={styles.resizeHandle}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${typeof column.label === 'string' ? column.label : columnKey} column`}
                          onMouseDown={(e) => startColumnResize(e, columnKey)}
                          onClick={(e) => e.stopPropagation()}
                          draggable={false}
                          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        />
                      ) : undefined}
                      sortDirection={
                        isSorted && !isTextColumn ? sortDirection : undefined
                      }
                      // Expose sort state to screen readers on every sortable
                      // header, including text columns (which show an A–Z/Z–A
                      // label instead of Fluent's sortDirection arrow). Spread
                      // last in Fluent's root slot, so this wins over its
                      // computed aria-sort without rendering the sort icon.
                      aria-sort={isSorted ? sortDirection : undefined}
                      draggable={isDraggable}
                      onDragStart={isDraggable ? (e) => {
                        setDragColumnKey(columnKey);
                        e.dataTransfer.effectAllowed = 'move';
                        // Firefox won't start a native drag unless data is set.
                        e.dataTransfer.setData('text/plain', columnKey);
                      } : undefined}
                      onDragOver={isDraggable ? (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverColumnKey !== columnKey) setDragOverColumnKey(columnKey);
                      } : undefined}
                      onDragLeave={isDraggable ? () => {
                        setDragOverColumnKey((prev) => (prev === columnKey ? null : prev));
                      } : undefined}
                      onDrop={isDraggable ? (e) => {
                        e.preventDefault();
                        handleColumnDrop(columnKey);
                      } : undefined}
                      onDragEnd={isDraggable ? () => {
                        setDragColumnKey(null);
                        setDragOverColumnKey(null);
                      } : undefined}
                      className={mergeClasses(
                        column.columnKey === 'checkbox' ? styles.checkboxCell : styles.headerCell,
                        isDraggable && styles.reorderableHeader,
                        isDraggable && dragColumnKey === columnKey && styles.draggingHeader,
                        isDraggable && !!dragColumnKey && dragColumnKey !== columnKey
                          && dragOverColumnKey === columnKey && styles.dragOverHeader,
                      )}
                    >
                      {column.columnKey === 'checkbox' ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Checkbox
                            checked={allCurrentPageSelected}
                            onChange={(e, data) => handleSelectAll(data.checked === true)}
                          />
                        </div>
                      ) : (column.sortable === false && column.filterable === false && column.groupable === false) ? (
                        // Fully non-interactive header — no menu, no chevron.
                        // Used for columns like the tags badge column where
                        // there's no meaningful sort/filter/group operation.
                        <div className={styles.headerContent}>
                          <span className={styles.headerLabel}>{column.label}</span>
                        </div>
                      ) : (column.sortable !== false
                          && column.filterable === false
                          && column.groupable === false) ? (
                        // Sort-only column: click toggles asc/desc directly —
                        // the dropdown's only two items would be the sort
                        // directions, so skip the menu chrome.
                        <div
                          className={styles.headerContent}
                          onClick={() => {
                            const nextDirection: SortDirection =
                              sortColumn === column.columnKey && sortDirection === 'ascending'
                                ? 'descending'
                                : 'ascending';
                            handleSort(columnKey, nextDirection);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const nextDirection: SortDirection =
                                sortColumn === column.columnKey && sortDirection === 'ascending'
                                  ? 'descending'
                                  : 'ascending';
                              handleSort(columnKey, nextDirection);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Sort by ${typeof column.label === 'string' ? column.label : columnKey}`}
                        >
                          <span className={styles.headerLabel}>{column.label}</span>
                          {textSortLabel && (
                            <span className={styles.textSortIndicator}>{textSortLabel}</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <Menu>
                            <MenuTrigger disableButtonEnhancement>
                              <div className={styles.headerContent}>
                                <span className={styles.headerLabel}>{column.label}</span>
                                {textSortLabel && (
                                  <span className={styles.textSortIndicator}>{textSortLabel}</span>
                                )}
                                <ChevronDown16Regular />
                              </div>
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                {(() => {
                                  const ct = columnTypes[String(column.columnKey)];
                                  const isDate = !!ct?.isDate;
                                  const isNumber = !!ct?.isNumber;

                                  let ascendingLabel = 'A to Z';
                                  let descendingLabel = 'Z to A';

                                  if (isDate) {
                                    ascendingLabel = 'Older to newer';
                                    descendingLabel = 'Newer to older';
                                  } else if (isNumber) {
                                    ascendingLabel = 'Ascending';
                                    descendingLabel = 'Descending';
                                  }

                                  return (
                                    <>
                                      <MenuItem onClick={() => handleSort(columnKey, 'ascending')}>
                                        {ascendingLabel}
                                      </MenuItem>
                                      <MenuItem onClick={() => handleSort(columnKey, 'descending')}>
                                        {descendingLabel}
                                      </MenuItem>
                                    </>
                                  );
                                })()}
                                <MenuDivider />
                                {column.filterable !== false && (
                                  <MenuItem onClick={() => {
                                    // Get current filters for this column
                                    const currentColumnFilters = columnFilters.get(String(column.columnKey));
                                    const currentFiltersArray = currentColumnFilters ? Array.from(currentColumnFilters) : [];

                                    // Trigger filter drawer
                                    window.dispatchEvent(new CustomEvent('openFilterDrawer', {
                                      detail: {
                                        columnKey: column.columnKey,
                                        columnLabel: column.label,
                                        data: data,
                                        currentFilters: currentFiltersArray,
                                      }
                                    }));
                                  }}>
                                    Filter by
                                  </MenuItem>
                                )}
                                {column.groupable !== false && (
                                  <MenuItem onClick={() => handleGroupBy(columnKey)}>
                                    {groupByColumn === column.columnKey ? '✓ ' : ''}Group by {column.label}
                                  </MenuItem>
                                )}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        </>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnsWithMenu.length}>
                    <TableCellLayout>No data found</TableCellLayout>
                  </TableCell>
                </TableRow>
              ) : groupedData ? (
                // Render grouped data — pagination is bypassed when grouping
                // is active so every group header is visible on one screen.
                // Users collapse groups they don't care about to scan quickly;
                // paginating *within* groups would chop headers across pages
                // and defeat the point of grouping.
                Object.entries(groupedData).map(([groupValue, groupItems]) => {
                  const visibleGroupItems = groupItems;
                  const isExpanded = expandedGroups.has(groupValue);

                  return (
                    <React.Fragment key={groupValue}>
                      <TableRow>
                        <TableCell
                          colSpan={columnsWithMenu.length}
                          className={styles.groupHeader}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onClick={() => toggleGroupExpansion(groupValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleGroupExpansion(groupValue);
                            }
                          }}
                        >
                          <div className={styles.groupHeaderContent}>
                            <div className={styles.groupExpandButton}>
                              {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                            </div>
                            {getGroupHeaderIcon(groupByColumn) && (
                              <div className={styles.groupHeaderIcon}>
                                {getGroupHeaderIcon(groupByColumn)}
                              </div>
                            )}
                            <span>
                              {formatGroupLabel(groupValue, groupByColumn)}
                              {showGroupCount ? ` (${groupItems.length})` : ''}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && visibleGroupItems.map((item) => {
                        const itemKey = String(item[keyField]);
                        const isRowHovered = hoveredRowKey === itemKey;

                        return (
                          <TableRow
                            key={itemKey}
                            onMouseEnter={() => setHoveredRowKey(itemKey)}
                            onMouseLeave={() => setHoveredRowKey(null)}
                            onClick={onRowClick ? () => onRowClick(item) : undefined}
                            role={onRowClick ? 'button' : undefined}
                            tabIndex={onRowClick ? 0 : undefined}
                            onKeyDown={onRowClick ? (e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                                e.preventDefault();
                                onRowClick(item);
                              }
                            } : undefined}
                            className={onRowClick ? styles.clickableRow : ''}
                          >
                            {columnsWithMenu.map((column) => {
                              const columnKey = String(column.columnKey);
                              const isPrimaryColumn = column.columnKey === effectivePrimaryColumn;

                              if (column.columnKey === 'checkbox') {
                                return (
                                  <TableCell key="checkbox" className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                                    <TableCellLayout>
                                      <Checkbox
                                        checked={selectedKeys.has(itemKey)}
                                        onChange={(e, data) => handleSelectOne(itemKey, data.checked === true)}
                                      />
                                    </TableCellLayout>
                                  </TableCell>
                                );
                              }

                              return (
                                <TableCell
                                  key={columnKey}
                                  className={styles.tableCell}
                                >
                                    {isPrimaryColumn && rowMenuItems && rowMenuItems.length > 0 ? (
                                      <div className={styles.primaryCellContent}>
                                        <div className={styles.primaryCellMain}>
                                          {column.render ? column.render(item) : String(item[column.columnKey])}
                                        </div>
                                        {renderEllipsisMenu(item, isRowHovered)}
                                      </div>
                                    ) : (
                                      <div className={styles.cellContent}>
                                        {column.render ? column.render(item) : String(item[column.columnKey])}
                                      </div>
                                    )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              ) : (
                // Render normal ungrouped data
                paginatedData.map((item) => {
                  const itemKey = String(item[keyField]);
                  const isRowHovered = hoveredRowKey === itemKey;

                  return (
                    <TableRow
                      key={itemKey}
                      onMouseEnter={() => setHoveredRowKey(itemKey)}
                      onMouseLeave={() => setHoveredRowKey(null)}
                      onClick={onRowClick ? () => onRowClick(item) : undefined}
                      role={onRowClick ? 'button' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onKeyDown={onRowClick ? (e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      } : undefined}
                      className={onRowClick ? styles.clickableRow : ''}
                    >
                      {columnsWithMenu.map((column) => {
                        const columnKey = String(column.columnKey);
                        const isPrimaryColumn = column.columnKey === effectivePrimaryColumn;

                        if (column.columnKey === 'checkbox') {
                          return (
                            <TableCell key="checkbox" className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                              <TableCellLayout>
                                <Checkbox
                                  checked={selectedKeys.has(itemKey)}
                                  onChange={(e, data) => handleSelectOne(itemKey, data.checked === true)}
                                />
                              </TableCellLayout>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={columnKey}
                            className={styles.tableCell}
                          >
                            {isPrimaryColumn && rowMenuItems && rowMenuItems.length > 0 ? (
                              <div className={styles.primaryCellContent}>
                                <div className={styles.primaryCellMain}>
                                  {column.render ? column.render(item) : String(item[column.columnKey])}
                                </div>
                                {renderEllipsisMenu(item, isRowHovered)}
                              </div>
                            ) : (
                              <div className={styles.cellContent}>
                                {column.render ? column.render(item) : String(item[column.columnKey])}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        )}

      {/* Sticky Footer with Pagination — hidden when grouping is active
          since every group is rendered at once (no per-page slicing). */}
      {!groupByColumn && (
        <div className={styles.stickyFooter}>
          <Pagination
            currentPage={currentPage}
            totalItems={sortedData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );

  return disableWrapper ? tableContent : (
    <div className={styles.wrapper}>
      {tableContent}
    </div>
  );
}
